export const VAT_RATE_PERCENT = 25;

export interface TaxBreakdownLine {
  description?: string;
  quantity?: number;
  unit_price?: number;
  unit_type?: string;
  amount_excl_vat: number;
  vat_amount: number;
  amount_incl_vat: number;
}

export interface TaxBreakdown {
  amount_incl_vat: number;
  amount_excl_vat: number;
  vat_amount: number;
  vat_rate: number;
  lines?: TaxBreakdownLine[];
}

/** Compute Swedish moms breakdown from a gross (inkl. moms) amount. */
export function computeTaxBreakdownFromGross(
  amountInclVat: number,
  rate = VAT_RATE_PERCENT
): TaxBreakdown {
  const amount = Math.max(0, Math.round(amountInclVat));
  if (amount === 0) {
    return {
      amount_incl_vat: 0,
      amount_excl_vat: 0,
      vat_amount: 0,
      vat_rate: rate,
      lines: [],
    };
  }

  const vat_amount = Math.round((amount * rate) / (100 + rate));
  const amount_excl_vat = amount - vat_amount;

  return {
    amount_incl_vat: amount,
    amount_excl_vat,
    vat_amount,
    vat_rate: rate,
    lines: [],
  };
}

export function computeTaxBreakdownFromLines(
  lines: Array<{ quantity: number; unit_price: number }>,
  rate = VAT_RATE_PERCENT
): TaxBreakdown {
  const breakdownLines = lines.map((line) => {
    const gross = line.quantity * line.unit_price;
    const tax = computeTaxBreakdownFromGross(gross, rate);
    return {
      amount_excl_vat: tax.amount_excl_vat,
      vat_amount: tax.vat_amount,
      amount_incl_vat: tax.amount_incl_vat,
    };
  });

  return {
    amount_incl_vat: breakdownLines.reduce((s, l) => s + l.amount_incl_vat, 0),
    amount_excl_vat: breakdownLines.reduce((s, l) => s + l.amount_excl_vat, 0),
    vat_amount: breakdownLines.reduce((s, l) => s + l.vat_amount, 0),
    vat_rate: rate,
    lines: breakdownLines,
  };
}
